// Agents gallery — cards for each seeded agent, plus a templates row.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Rocket, Search } from 'lucide-react';
import type { AgentListItem } from '@qualibot/shared';
import { Card } from '@/components/primitives/Card';
import { Badge } from '@/components/primitives/Badge';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Btn } from '@/components/primitives/Btn';
import { Empty } from '@/components/primitives/Empty';
import { PageContainer, PageHeader, SectionLabel } from '@/components/PageHeader';
import { listAgents } from '@/lib/agents-api';
import { humanizeEnum } from '@/lib/format';

const TEMPLATES = [
  'DMS Assistant',
  'HR Assistant',
  'Hiring Bot',
  'Appointment',
  'Support Bot',
] as const;

export function AgentsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | AgentListItem['status']>('ALL');
  const [channelFilter, setChannelFilter] = useState<string>('ALL');

  const query = useQuery({ queryKey: ['agents'], queryFn: listAgents });

  const visible = useMemo(() => {
    if (!query.data) return [];
    return query.data.filter((a) => {
      if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== 'ALL' && a.status !== statusFilter) return false;
      if (channelFilter !== 'ALL' && !a.channels.includes(channelFilter)) return false;
      return true;
    });
  }, [query.data, search, statusFilter, channelFilter]);

  return (
    <PageContainer>
      <PageHeader
        title="Agents"
        description="Create, test, deploy and monitor your AI agents"
        actions={
          <Btn kind="primary" icon={Plus} onClick={() => navigate('/agents/new')}>
            Create agent
          </Btn>
        }
      />

      <div className="flex items-center gap-2.5 mb-5">
        <div className="relative w-[280px]">
          <Search size={14} className="absolute left-3 top-[10px] text-text-3 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 pl-8 pr-3 rounded-[10px] border border-border-strong bg-surface text-[13px] outline-none focus:border-accent"
            placeholder="Search agents…"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className="h-9 w-[150px] rounded-[10px] border border-border-strong bg-surface px-3 text-[13px]"
        >
          <option value="ALL">Status: All</option>
          <option value="LIVE">Live</option>
          <option value="DRAFT">Draft</option>
          <option value="PAUSED">Paused</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className="h-9 w-[150px] rounded-[10px] border border-border-strong bg-surface px-3 text-[13px]"
        >
          <option value="ALL">Channel: All</option>
          <option value="WhatsApp">WhatsApp</option>
          <option value="Web Chat">Web Chat</option>
          <option value="SMS">SMS</option>
        </select>
      </div>

      {query.isLoading ? (
        <div className="text-[13px] text-text-3 py-10 text-center">Loading agents…</div>
      ) : query.isError ? (
        <Card>
          <Empty
            title="Couldn't load agents"
            body="The agents service is unreachable."
            actions={<Btn onClick={() => query.refetch()}>Retry</Btn>}
          />
        </Card>
      ) : visible.length === 0 ? (
        <Card>
          <Empty
            title="No agents match"
            body="Adjust your filters, or create a new agent."
            actions={
              <Btn kind="primary" icon={Plus} onClick={() => navigate('/agents/new')}>
                Create agent
              </Btn>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-3.5">
          {visible.map((a) => (
            <AgentCard key={a.id} a={a} onOpen={() => navigate(`/agents/${a.id}`)} />
          ))}
        </div>
      )}

      <SectionLabel>Start from a template</SectionLabel>
      <div className="grid grid-cols-4 gap-3.5">
        {TEMPLATES.map((t) => (
          <Card key={t} pad={16}>
            <div className="flex items-center justify-between">
              <span className="text-[13.5px] font-medium">{t}</span>
              <Btn size="sm" onClick={() => navigate('/agents/new')}>
                Use
              </Btn>
            </div>
          </Card>
        ))}
      </div>
    </PageContainer>
  );
}

function AgentCard({ a, onOpen }: { a: AgentListItem; onOpen: () => void }) {
  return (
    <Card pad={0}>
      <div className="px-[18px] pt-4 pb-3.5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{
                background: a.status === 'LIVE' ? 'var(--ok)' : 'var(--text-3)',
              }}
            />
            <span className="font-semibold text-[14.5px]">{a.name}</span>
          </div>
          <StatusBadge status={humanizeEnum(a.status)} />
        </div>
        <div className="text-[13px] text-text-2 min-h-[38px]">{a.description}</div>
        <div className="flex items-center flex-wrap gap-1.5 mt-3">
          {a.channels.map((c) => (
            <Badge key={c}>{c}</Badge>
          ))}
          {a.languages.length > 0 && <Badge>{a.languages.join(' · ')}</Badge>}
          <Badge>{a.skillCount} skills</Badge>
          <Badge>{a.mcpCount} MCP tools</Badge>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-border px-[18px] py-2.5">
        <span className="text-[12px] text-text-3">{a.lastActiveLabel}</span>
        <div className="flex items-center gap-1.5">
          <Btn size="sm" onClick={onOpen}>
            Edit
          </Btn>
          <Btn size="sm" onClick={onOpen}>
            Skills
          </Btn>
          {a.status === 'DRAFT' ? (
            <Btn size="sm" kind="primary" icon={Rocket}>
              Deploy
            </Btn>
          ) : (
            <Btn size="sm">Logs</Btn>
          )}
        </div>
      </div>
    </Card>
  );
}
